/**
 * What To Rent Out Page - category + pricing-guidance content page (SEO
 * Phase 2, docs/design/seo.md — owner-approved copy 2026-07-03). Targets
 * "make money renting party supplies" query space. Earnings talk is
 * deliberately pricing GUIDANCE, not income claims: pre-launch we have no
 * booking history to back "hosts earn $X" statements (fail honest).
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import { logFunnelEvent } from '../../util/analytics';
import { Page, LayoutSingleColumn } from '../../components';
import { NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './WhatToRentOutPage.module.css';

const CATEGORIES = [
  {
    heading: 'Themed birthday kits',
    body:
      "Dinosaur, princess, superhero, farmyard. Complete kids' birthday looks are our most requested category.",
  },
  {
    heading: 'Baby shower and gender reveal setups',
    body:
      'Soft palettes, floral arches, welcome signs. Parents-to-be host these once and rarely want to own the decor.',
  },
  {
    heading: 'Backdrops and balloon garlands',
    body:
      'Reusable frames, faux-flower walls, and garland kits photograph beautifully and pack small.',
  },
  {
    heading: 'Tableware and place settings',
    body:
      'Real plates, cloth linens, and glassware for dinner parties. Renters love skipping the paper-plate look without buying a set for twelve.',
  },
  {
    heading: 'Picnic and date-night setups',
    body:
      'Low tables, cushions, lanterns, and candles for beach picnics and backyard dinners. A natural fit for Santa Barbara.',
  },
  {
    heading: 'Games and extras',
    body:
      'Lawn games, photo props, string lights, and anything that makes a party more fun.',
  },
];

export const WhatToRentOutPageComponent = props => {
  const { history, isAuthenticated } = props;

  useEffect(() => {
    logFunnelEvent('page_view_what_to_rent_out');
  }, []);

  // Same auth-aware CTA routing as VendorStarterGuidePage.
  const handleCtaClick = () => {
    history.push(isAuthenticated ? '/l/new' : '/signup');
  };

  // Brand suffix appended centrally in Page.js (single + consistent).
  const title = 'What Party Supplies Can You Rent Out?';
  const description =
    'Themed birthday kits, baby shower decor, backdrops, tableware, picnic setups and more: what rents well on Merrily in the Santa Barbara area, and how to price it.';

  return (
    <Page
      className={css.root}
      title={title}
      description={description}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.main}>
          <article className={css.container}>
            <h1 className={css.title}>What party supplies can you rent out?</h1>
            <p className={css.intro}>
              If people would buy it for one party and store it forever after, someone would
              rather rent it. Here is what works on Merrily.
            </p>

            {CATEGORIES.map(c => (
              <section key={c.heading} className={css.category}>
                <h2 className={css.categoryHeading}>{c.heading}</h2>
                <p className={css.categoryBody}>{c.body}</p>
              </section>
            ))}

            <section className={css.pricing}>
              <h2 className={css.pricingHeading}>What should you charge?</h2>
              <p className={css.categoryBody}>
                You set your own price. A useful starting point is 10 to 20% of what the kit
                would cost to buy new, per day. A kit that would cost $400 to assemble might
                rent for $40 to $80 per day.{' '}
                <NamedLink name="SearchPage" className={css.inlineLink}>
                  Browse the kits currently listed
                </NamedLink>{' '}
                to see real prices in your area.
              </p>
            </section>

            <div className={css.cta}>
              <button className={css.ctaButton} onClick={handleCtaClick}>
                List your first kit
              </button>
              <p className={css.crossLinks}>
                New to hosting? Read{' '}
                <NamedLink name="HostingGuidePage" className={css.inlineLink}>
                  how renting out your party supplies works
                </NamedLink>{' '}
                or the{' '}
                <NamedLink name="VendorStarterGuidePage" className={css.inlineLink}>
                  Become a Host overview
                </NamedLink>
                .
              </p>
            </div>
          </article>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  isAuthenticated: !!state.auth?.isAuthenticated,
});

const WhatToRentOutPage = compose(
  withRouter,
  connect(mapStateToProps)
)(WhatToRentOutPageComponent);

export default WhatToRentOutPage;
